/**
 * Showtime V2 - Global JavaScript
 *
 * Core utilities and components loaded on all pages:
 * - Utility functions (debounce, trapFocus, announce)
 * - Mobile menu
 * - Desktop navigation
 * - Cart quantity (used in header cart icon count)
 *
 * Progressive enhancement only - core functionality works without JS.
 * No external dependencies.
 */

(function() {
  'use strict';

  // JS enabled flag for progressive enhancement hooks
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /**
   * Debounce utility
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Milliseconds to wait
   * @returns {Function}
   */
  function debounce(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Trap focus within an element
   * @param {HTMLElement} element - Container element
   */
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }

    element.addEventListener('keydown', handleKeyDown);

    return () => element.removeEventListener('keydown', handleKeyDown);
  }

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   */
  function announce(message) {
    const region = document.getElementById('aria-live-region');
    if (region) {
      region.textContent = message;
      setTimeout(() => {
        region.textContent = '';
      }, 1000);
    }
  }

  /**
   * Mobile Menu Controller
   */
  class MobileMenu {
    constructor() {
      this.menu = document.getElementById('mobile-menu');
      this.toggleBtn = document.querySelector('.header__menu-toggle');
      this.closeBtn = document.querySelector('.mobile-menu__close');
      // Select all submenu toggles (both first and second level)
      this.submenuToggles = document.querySelectorAll('[data-mobile-submenu-toggle]');

      if (!this.menu || !this.toggleBtn) return;

      this.isOpen = false;
      this.removeFocusTrap = null;

      this.init();
    }

    init() {
      this.toggleBtn.addEventListener('click', () => this.toggle());

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Submenu toggles - click and keyboard support
      this.submenuToggles.forEach(toggle => {
        toggle.addEventListener('click', () => this.toggleSubmenu(toggle));

        // Keyboard support for Enter and Space on submenu toggles
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleSubmenu(toggle);
          }
        });
      });

      // Close when clicking outside
      document.addEventListener('click', (e) => {
        if (this.isOpen && !this.menu.contains(e.target) && !this.toggleBtn.contains(e.target)) {
          this.close();
        }
      });
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.isOpen = true;
      this.menu.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Focus first focusable element
      const firstFocusable = this.menu.querySelector('button, [href], input');
      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Trap focus
      this.removeFocusTrap = trapFocus(this.menu);
    }

    close() {
      this.isOpen = false;
      this.menu.hidden = true;
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      // Return focus to toggle button
      this.toggleBtn.focus();

      // Remove focus trap
      if (this.removeFocusTrap) {
        this.removeFocusTrap();
        this.removeFocusTrap = null;
      }
    }

    toggleSubmenu(toggle) {
      const submenu = toggle.nextElementSibling;
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

      toggle.setAttribute('aria-expanded', !isExpanded);
      submenu.hidden = isExpanded;
    }
  }

  /**
   * Desktop Navigation Dropdown Controller
   * Handles mega menu dropdowns with full keyboard support (WCAG 2.1 AA)
   */
  class DesktopNav {
    constructor() {
      // Match items with mega menus (has-mega-menu class from header.liquid)
      this.navItems = document.querySelectorAll('.header__nav-item.has-mega-menu');

      if (!this.navItems.length) return;

      this.init();
    }

    init() {
      this.navItems.forEach(item => {
        const toggle = item.querySelector('[data-mega-menu-trigger]');
        const dropdown = item.querySelector('.mega-menu');

        if (!toggle || !dropdown) return;

        // Click support (works for both mouse and keyboard-triggered clicks)
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDropdown(toggle, dropdown);
        });

        // Keyboard support for Enter and Space
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleDropdown(toggle, dropdown);
          }
          // Arrow down opens dropdown and moves focus to first item
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.openDropdown(toggle, dropdown);
            this.focusFirstItem(dropdown);
          }
          if (e.key === 'Escape') {
            this.closeDropdown(toggle);
            toggle.focus();
          }
        });

        // Keyboard navigation within dropdown
        dropdown.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            this.closeDropdown(toggle);
            toggle.focus();
          }
          // Tab out of dropdown closes it
          if (e.key === 'Tab' && !e.shiftKey) {
            const focusableItems = dropdown.querySelectorAll('a[href], button:not([disabled])');
            const lastItem = focusableItems[focusableItems.length - 1];
            if (document.activeElement === lastItem) {
              this.closeDropdown(toggle);
            }
          }
        });

        // Close when mouse leaves the nav item (preserve hover behavior)
        item.addEventListener('mouseleave', () => {
          this.closeDropdown(toggle);
        });

        // Close on focus out (when focus moves outside the nav item)
        item.addEventListener('focusout', (e) => {
          // Use setTimeout to allow focus to move before checking
          setTimeout(() => {
            if (!item.contains(document.activeElement)) {
              this.closeDropdown(toggle);
            }
          }, 0);
        });
      });

      // Close all dropdowns on escape (global handler)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.navItems.forEach(item => {
            const toggle = item.querySelector('[data-mega-menu-trigger]');
            if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
              this.closeDropdown(toggle);
              toggle.focus();
            }
          });
        }
      });
    }

    toggleDropdown(toggle, dropdown) {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

      // Close other dropdowns first
      this.navItems.forEach(item => {
        const otherToggle = item.querySelector('[data-mega-menu-trigger]');
        if (otherToggle && otherToggle !== toggle) {
          this.closeDropdown(otherToggle);
        }
      });

      if (isExpanded) {
        this.closeDropdown(toggle);
      } else {
        this.openDropdown(toggle, dropdown);
      }
    }

    openDropdown(toggle, dropdown) {
      toggle.setAttribute('aria-expanded', 'true');
      // Announce to screen readers
      announce('Menu expanded');
    }

    closeDropdown(toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }

    focusFirstItem(dropdown) {
      const firstLink = dropdown.querySelector('a[href], button:not([disabled])');
      if (firstLink) {
        firstLink.focus();
      }
    }
  }

  /**
   * Cart Quantity Handler
   */
  class CartQuantity {
    constructor() {
      this.quantityInputs = document.querySelectorAll('.main-cart__quantity-input');

      if (!this.quantityInputs.length) return;

      this.init();
    }

    init() {
      // Auto-submit on quantity change (debounced)
      const debouncedSubmit = debounce(() => {
        const form = document.getElementById('cart-form');
        if (form) {
          form.submit();
        }
      }, 500);

      this.quantityInputs.forEach(input => {
        input.addEventListener('change', debouncedSubmit);
      });
    }
  }

  /**
   * Initialize global components when DOM is ready
   */
  function init() {
    // Mobile menu
    new MobileMenu();

    // Desktop navigation
    new DesktopNav();

    // Cart quantity (if on cart page)
    new CartQuantity();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utilities for potential use by other scripts
  window.ShowtimeTheme = {
    debounce,
    trapFocus,
    announce
  };

})();
